import type { BudgetUnit } from "../../api/entitlementBudgets";

/** Whole US dollars, as the admin and request pages show money. */
const wholeDollars = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** Formats whole USD, for example `$1,200`. */
export function formatWholeUsd(amount: number): string {
  return wholeDollars.format(amount);
}

/** USD as whole dollars when it is a whole amount, otherwise to the cent: an allowance is usually whole but is stored with cents. */
export function formatUsd(amount: number): string {
  return Number.isInteger(amount) ? wholeDollars.format(amount) : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

/** The key a unit's budget is looked up by. Ids are compared without regard to case, as the server writes them in lower case. */
export function budgetKey(kind: string, id: string): string {
  return `${kind}:${id.toLowerCase()}`;
}

/**
 * Whether a unit's hard stop is inherited: it is on, and it comes from another unit (an ancestor), so this unit cannot change it
 * (SPEC R-11a). A unit whose own setting is the source is not inherited.
 */
export function isHardStopInherited(unit: BudgetUnit): boolean {
  return unit.effectiveHardStop && (unit.hardStopSourceId ?? "").toLowerCase() !== unit.id.toLowerCase();
}

/**
 * Whether a unit's Capex mark is inherited: the unit is Capex, and the mark comes from another unit (an ancestor), so this unit cannot
 * change it. A unit whose own mark is the source is not inherited.
 */
export function isCapexInherited(unit: BudgetUnit): boolean {
  return unit.effectiveCapex && (unit.capexSourceId ?? "").toLowerCase() !== unit.id.toLowerCase();
}

/** Parses the editor's amount: blank is null (clear), otherwise a whole number of dollars from 0 to 9,999,999. Returns undefined when invalid. */
export function parseBudgetAmount(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  const value = Number(trimmed);
  return Number.isInteger(value) && value >= 0 && value <= 9999999 ? value : undefined;
}

/** The date part of an ISO timestamp, as `YYYY-MM-DD`. */
export function isoDatePart(timestamp: string): string {
  return timestamp.slice(0, 10);
}
